// HAPPYCAP_RECURSOS_AZUL_FRESCO_V1
import { useEffect, useState } from "react";
import { getProyectos } from "../services/api";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const ProyectosPage = () => {
  const { t } = useTranslation();
  const [originalProyectos, setOriginalProyectos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [paises, setPaises] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [paisSeleccionado, setPaisSeleccionado] = useState("");
  const [ciudadSeleccionada, setCiudadSeleccionada] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProyectos = async () => {
      setLoading(true);
      try {
        const res = await getProyectos();
        setOriginalProyectos(res.data);
        setProyectos(res.data);

        const allPaises = [...new Set(res.data.map(p => p.pais).filter(Boolean))];
        const allCiudades = [...new Set(res.data.map(p => p.ciudad).filter(Boolean))];

        setPaises(allPaises);
        setCiudades(allCiudades);
        setErrorMsg("");
      } catch (error) {
        console.error("Error al cargar proyectos", error);
        setErrorMsg(t("research.error"));
      } finally {
        setLoading(false);
      }
    };

    fetchProyectos();
  }, []);

  useEffect(() => {
    const filtrados = originalProyectos.filter((p) => {
      const matchPais = paisSeleccionado ? p.pais === paisSeleccionado : true;
      const matchCiudad = ciudadSeleccionada ? p.ciudad === ciudadSeleccionada : true;
      return matchPais && matchCiudad;
    });

    setProyectos(filtrados);

    const ciudadesFiltradas = originalProyectos
      .filter(p => (paisSeleccionado ? p.pais === paisSeleccionado : true))
      .map(p => p.ciudad)
      .filter(Boolean);

    setCiudades([...new Set(ciudadesFiltradas)]);
  }, [paisSeleccionado, ciudadSeleccionada, originalProyectos]);

  const sortByTranslation = (arr) =>
    [...arr].sort((a, b) =>
      t(`countries.${a}`, a).localeCompare(t(`countries.${b}`, b), undefined, { sensitivity: "base" })
    );

  const sortStrings = (arr) =>
    [...arr].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );

  return (
    <main className="max-w-5xl mx-auto p-6 mt-2 md:mt-5" aria-label={t("research.title")}>
      <div className="flex flex-row items-center justify-between flex-wrap gap-4 mb-6">
        <h2 className="text-2xl font-bold text-[#247BA8] flex-1">
          {t("research.title")}
        </h2>

        <button
          onClick={() => navigate("/recursos")}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#247BA8] text-white shadow-sm transition hover:bg-[#1B6388] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#247BA8]/30 focus-visible:ring-offset-2"
          aria-label={t("resources.view_resources")}
        >
          <ArrowLeft size={20} />
        </button>
      </div>

      <fieldset className="mb-10 flex flex-wrap gap-4" aria-label={t("research.filters")}>
        <legend className="sr-only">{t("research.filters")}</legend>

        <div className="w-full min-w-0 sm:w-56">
          <select
            id="select-country"
            value={paisSeleccionado}
            onChange={(e) => {
              setPaisSeleccionado(e.target.value);
              setCiudadSeleccionada("");
            }}
            className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-700 shadow-sm outline-none transition hover:border-[#B8D8EA] focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8]/15 sm:text-[15px]"
          >
            <option value="">{t("research.country", "País")}</option>
            {sortByTranslation(paises).map((p) => (
              <option key={p} value={p}>
                {t(`countries.${p}`, p)}
              </option>
            ))}
          </select>
        </div>

        {ciudades.length > 0 && (
          <div className="w-full min-w-0 sm:w-56">
            <select
              id="select-city"
              value={ciudadSeleccionada}
              onChange={(e) => setCiudadSeleccionada(e.target.value)}
              className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-700 shadow-sm outline-none transition hover:border-[#B8D8EA] focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8]/15 sm:text-[15px]"
            >
              <option value="">{t("research.city", "Ciudad")}</option>
              {sortStrings(ciudades).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}
      </fieldset>

      {errorMsg && (
        <p className="text-red-600" role="alert" aria-live="assertive">
          {errorMsg}
        </p>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center mt-10 text-gray-600" role="status" aria-live="polite">
          <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mb-3" />
          <p className="text-sm">{t("research.loading")}</p>
        </div>
      ) : proyectos.length === 0 ? (
        null
      ) : (
        <section aria-label={t("research.results")}>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {proyectos.map((p) => (
              <article
                key={p.id}
                className="bg-white shadow p-4 rounded-lg hover:shadow-md transition-shadow duration-200"
                aria-label={p.titulo}
              >
                <h3 className="font-semibold text-blue-700 mb-1">{p.titulo}</h3>
                <p>
                  <strong>{t("research.country")}:</strong>{" "}
                  {t(`countries.${p.pais}`, p.pais)}
                </p>
                {p.ciudad && (
                  <p>
                    <strong>{t("research.city")}:</strong> {p.ciudad}
                  </p>
                )}
                {p.enlace && (
                  <a
                    href={p.enlace}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-2 text-blue-500 hover:underline"
                  >
                    {t("research.more_info")}
                  </a>
                )}
              </article>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProyectosPage;
